<template>
  <div v-if="kpiStats" class="flex flex-column gap-2">
    <div class="list" v-if="hasFeature('view_shifts')">
      <div class="item">
        <h3>Shifts</h3>
        <div class="grid">
          <b class="col-6">Paid</b>
          <span class="col-6 text-right">{{ paidShiftsCount }}</span>

          <b class="col-6">Manual</b>
          <span class="col-6 text-right">{{ kpiStats.manually_created_shifts }}</span>

          <b class="col-6">With Violations</b>
          <span class="col-6 text-right">{{ kpiStats.shifts_with_auths }}</span>

          <b class="col-6">Total Hours</b>
          <span class="col-6 text-right">{{ kpiStats.total_hours }}</span>

          <b class="col-6">Worked Shifts</b>
          <span class="col-6 text-right">{{ kpiStats.worked_shifts }}</span>
        </div>
      </div>

      <div class="item">
        <h3>Problems</h3>
        <div class="grid">
          <template v-if="hasFeature('authorization')"
            ><b class="col-6">Shifts with Violations</b>
            <span class="col-6 text-right">{{ kpiStats.shifts_with_auths }}</span></template
          >

          <b class="col-6">Written Warnings</b>
          <span class="col-6 text-right">{{ kpiStats.writeup_count }}</span>
        </div>
      </div>

      <div class="item" v-if="hasFeature('payment')">
        <h3>Payment</h3>
        <div class="grid">
          <b class="col-6">Accepted PRs</b>
          <span class="col-6 text-right">{{ kpiStats.accepted_payment_requests }}</span>

          <b class="col-6">Declined PRs</b>
          <span class="col-6 text-right">{{ kpiStats.declined_payment_requests }}</span>

          <b class="col-6">Received Money, $</b>
          <span class="col-6 text-right">{{ kpiStats.total_paid / 100 }}</span>
        </div>
      </div>
      <div class="item" v-if="hasFeature('task')">
        <h3>Tasks</h3>
        <div class="grid">
          <b class="col-6">Completed</b>
          <span class="col-6 text-right">{{ kpiStats.completed_tasks }}</span>

          <b class="col-6">Completed Assigned</b>
          <span class="col-6 text-right">{{ kpiStats.assigned_tasks }}</span>
        </div>
      </div>
    </div>
    <div class="flex gap-4 p-0" v-if="hasFeature('view_shifts')">
      <div class="flex flex-column gap-2">
        <h3>Shifts By Origin</h3>
        <bar-chart class="kpi-chart" :chart-data="originChartData" />
        <div class="grid">
          <b class="col-6">Time</b>
          <span class="col-6 text-right">{{ whereAmIShiftsCount }}</span>

          <b class="col-6">Schedule</b>
          <span class="col-6 text-right">{{ scheduleShiftsCount }}</span>

          <b class="col-6">Marketplace</b>
          <span class="col-6 text-right">{{ marketplaceShiftsCount }}</span>
        </div>
      </div>

      <div class="flex flex-column gap-2">
        <h3>Shifts By Status</h3>
        <bar-chart class="kpi-chart" :chart-data="shiftStatusesChartData" />
        <template v-if="shiftByStatuses && shiftByStatuses.length">
          <div class="grid" v-for="status in shiftByStatuses" :key="status.item">
            <b class="col-6">{{ getShiftStatusLabel(status.item) }}</b>
            <span class="col-6 text-right">{{ status.count }}</span>
          </div>
        </template>
        <span v-else>Information is not available</span>
      </div>
    </div>

    <div class="flex gap-4 p-0">
      <div class="flex flex-column gap-2" v-if="hasFeature('view_shifts')">
        <h3>Shifts By Payment Statuses</h3>
        <bar-chart class="kpi-chart" :chart-data="shiftPaymentStatusesChartData" />
        <template v-if="shiftByPaymentStatuses && shiftByPaymentStatuses.length">
          <div class="grid" v-for="status in shiftByPaymentStatuses" :key="status.item">
            <b class="col-6">{{ getPaymentStatusLabel(status.item) }}</b>
            <span class="col-6 text-right">{{ status.count }}</span>
          </div>
        </template>
        <span v-else>Information is not available</span>
      </div>

      <div class="flex flex-column gap-2" v-if="hasFeature('authorization')">
        <h3>Violations By Types</h3>
        <bar-chart class="kpi-chart" :chart-data="authsChartData" />
        <template v-if="authsByTypes && authsByTypes.length > 0">
          <div class="grid" v-for="auth in authsByTypes" :key="auth.item">
            <b class="col-6">{{ getAuthTypeLabel(auth.item) }}</b>
            <span class="col-6 text-right">{{ auth.count }}</span>
          </div>
        </template>
        <span v-else>Information is not available</span>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from '@/components/ui/bar-chart.vue'

import { authorizationsMap, shiftStatusesMap, paymentStatusesMap } from '@/const'
import featureMixin from '@/mixins/featureMixin'

const SHIFT_COUNT_LABEL = 'Shifts Count'

export default {
  props: ['kpiStats'],
  components: { 'bar-chart': BarChart },
  mixins: [featureMixin],
  computed: {
    whereAmIShiftsCount() {
      const foundWhereAmIShiftsInfo = this.kpiStats.shift_by_origins.find(origin => origin.item === 'whereami')

      return foundWhereAmIShiftsInfo ? foundWhereAmIShiftsInfo.count : 0
    },
    scheduleShiftsCount() {
      const foundScheduleShiftsInfo = this.kpiStats.shift_by_origins.find(origin => origin.item === 'schedule')

      return foundScheduleShiftsInfo ? foundScheduleShiftsInfo.count : 0
    },
    marketplaceShiftsCount() {
      const foundMarketplaceShiftsInfo = this.kpiStats.shift_by_origins.find(origin => origin.item === 'marketplace')

      return foundMarketplaceShiftsInfo ? foundMarketplaceShiftsInfo.count : 0
    },

    shiftByStatuses() {
      return this.kpiStats.shift_by_statuses
    },
    shiftByPaymentStatuses() {
      return this.kpiStats.shift_by_payment_statuses
    },

    paidShiftsCount() {
      const paidShiftsItem = this.shiftByPaymentStatuses.find(status => status.item === 'paid')

      return paidShiftsItem ? paidShiftsItem.count : 0
    },

    authsByTypes() {
      return this.kpiStats.authorization_by_types
    },

    originChartData() {
      const labels = []
      const data = []
      const colors = []

      if (this.whereAmIShiftsCount) {
        labels.push('Time')
        colors.push('#FF6384')
        data.push(this.whereAmIShiftsCount)
      }

      if (this.scheduleShiftsCount) {
        labels.push('Schedule')
        colors.push('#36A2EB')
        data.push(this.scheduleShiftsCount)
      }

      if (this.marketplaceShiftsCount) {
        labels.push('Marketplace')
        colors.push('#FFCE56')
        data.push(this.marketplaceShiftsCount)
      }

      return {
        labels,
        datasets: [
          {
            label: SHIFT_COUNT_LABEL,
            backgroundColor: colors,
            data
          }
        ]
      }
    },
    shiftStatusesChartData() {
      const labels = []
      const data = []
      const colors = []

      this.shiftByStatuses.forEach(st => {
        labels.push(this.getShiftStatusLabel(st.item))
        colors.push(this.getShiftStatusColor(st.item))
        data.push(st.count)
      })

      return {
        labels,
        datasets: [
          {
            label: SHIFT_COUNT_LABEL,
            backgroundColor: colors,
            data
          }
        ]
      }
    },
    shiftPaymentStatusesChartData() {
      const labels = []
      const data = []
      const colors = []

      this.shiftByPaymentStatuses.forEach(pt => {
        labels.push(this.getPaymentStatusLabel(pt.item))
        colors.push(this.getPaymentStatusColor(pt.item))
        data.push(pt.count)
      })

      return {
        labels,
        datasets: [
          {
            label: SHIFT_COUNT_LABEL,
            backgroundColor: colors,
            data
          }
        ]
      }
    },
    authsChartData() {
      const labels = []
      const data = []
      const colors = []

      this.authsByTypes.forEach(au => {
        labels.push(this.getAuthTypeLabel(au.item))
        colors.push(this.getAuthTypeColor(au.item))
        data.push(au.count)
      })

      return {
        labels,
        datasets: [
          {
            label: 'Violations Count',
            backgroundColor: colors,
            data
          }
        ]
      }
    }
  },
  methods: {
    getShiftStatusLabel(status) {
      return shiftStatusesMap[status] ? shiftStatusesMap[status].label : status
    },
    getPaymentStatusLabel(status) {
      return paymentStatusesMap[status] ? paymentStatusesMap[status].label : status
    },
    getAuthTypeLabel(type) {
      return authorizationsMap[type] ? authorizationsMap[type].label : type
    },

    getShiftStatusColor(status) {
      return shiftStatusesMap[status] ? shiftStatusesMap[status].color : '#bbbbbb'
    },
    getPaymentStatusColor(status) {
      return paymentStatusesMap[status] ? paymentStatusesMap[status].color : '#bbbbbb'
    },
    getAuthTypeColor(type) {
      return authorizationsMap[type] ? authorizationsMap[type].color : '#bbbbbb'
    }
  }
}
</script>

<style scoped>
.kpi-chart {
  width: 400px;
  margin-bottom: 10px;
}

.list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius);
}
</style>
